export const UNIFIED_ACCOUNT = 'UNIFIED_ACCOUNT';
export const DASHWORKS = 'DASHWORKS';
export const DATASYNC = 'DATASYNC';
export const MCP = 'MCP';
export const MICROSOFT_ADS = 'MICROSOFT_ADS';
export const MICROSOFT_DRIVE = 'MICROSOFT_DRIVE';
export const GOOGLE_SHEETS = 'GOOGLE_SHEETS';
export const ZOOMINFO = 'ZOOMINFO';
export const SLACK_INTEGRATION = 'SLACK_INTEGRATION';
export const SURVEY_MONKEY = 'SURVEY_MONKEY';
export const REDDIT_ADS = 'REDDIT_ADS';
export const CHATGPT_ADS = 'CHATGPT_ADS';
export const ADOBE_SIGN = 'ADOBE_SIGN';
export const GENERIC_OAUTH = 'GENERIC_OAUTH';
export const DEFAULT_PROVIDER_KEY = '__DEFAULT__';
export const PROVIDER_KEYS = {
  DEFAULT: DEFAULT_PROVIDER_KEY,
  UNIFIED_ACCOUNT,
  DASHWORKS,
  DATASYNC,
  MCP,
  MICROSOFT_ADS,
  MICROSOFT_DRIVE,
  GOOGLE_SHEETS,
  ZOOMINFO,
  SLACK_INTEGRATION,
  SURVEY_MONKEY,
  REDDIT_ADS,
  CHATGPT_ADS,
  ADOBE_SIGN,
  GENERIC_OAUTH
};
export function createAuthRegistry(options) {
  const componentsByKey = new Map();
  const providerKeyByAppId = new Map();
  const descriptors = new Map();
  const descriptorKeyByAppId = new Map();
  const loadingPromises = new Map();
  const failedLoads = new Set();
  let initialized = !(options !== null && options !== void 0 && options.initializer);
  // eslint-disable-next-line prefer-const -- assigned after function declarations that reference it
  let registry;
  function ensureInitialized() {
    if (!initialized) {
      initialized = true;
      options.initializer(registry);
    }
  }
  function registerProvider(providerKey, registration) {
    if ('components' in registration) {
      const {
        components,
        appIds = [],
        overwrite = false
      } = registration;
      if (componentsByKey.has(providerKey) && !overwrite) {
        console.warn(`[unified-auth] Provider "${providerKey}" already registered. Use overwrite: true to replace.`);
        return;
      }
      componentsByKey.set(providerKey, components);
      for (const appId of appIds) {
        providerKeyByAppId.set(Number(appId), providerKey);
      }
    } else {
      descriptors.set(providerKey, registration);
      for (const appId of registration.appIds) {
        descriptorKeyByAppId.set(Number(appId), providerKey);
      }
    }
  }
  function unregisterProvider(providerKey) {
    const existed = componentsByKey.delete(providerKey);
    descriptors.delete(providerKey);
    for (const [appId, key] of providerKeyByAppId) {
      if (key === providerKey) {
        providerKeyByAppId.delete(appId);
      }
    }
    for (const [appId, key] of descriptorKeyByAppId) {
      if (key === providerKey) {
        descriptorKeyByAppId.delete(appId);
      }
    }
    return existed;
  }
  function getProviderType(appId, providerKey) {
    ensureInitialized();
    if (providerKey) {
      if (componentsByKey.has(providerKey)) return 'sync';
      if (descriptors.has(providerKey)) return 'async';
    }
    const id = Number(appId);
    if (providerKeyByAppId.has(id)) return 'sync';
    if (descriptorKeyByAppId.has(id)) return 'async';
    if (componentsByKey.has(DEFAULT_PROVIDER_KEY)) return 'sync';
    return 'none';
  }
  function getComponentsSync(appId, providerKey) {
    ensureInitialized();
    if (providerKey) {
      return componentsByKey.get(providerKey);
    }
    const key = providerKeyByAppId.get(Number(appId));
    if (key) {
      return componentsByKey.get(key);
    }
    return componentsByKey.get(DEFAULT_PROVIDER_KEY);
  }
  function resolveComponents(appId, providerKey) {
    ensureInitialized();
    const id = Number(appId);
    const syncResult = providerKey ? componentsByKey.get(providerKey) : (() => {
      const key = providerKeyByAppId.get(id);
      return key ? componentsByKey.get(key) : undefined;
    })();
    if (syncResult) {
      return Promise.resolve(syncResult);
    }
    const resolverKey = providerKey !== null && providerKey !== void 0 ? providerKey : descriptorKeyByAppId.get(id);
    if (!resolverKey) {
      return Promise.resolve(componentsByKey.get(DEFAULT_PROVIDER_KEY));
    }
    if (componentsByKey.has(resolverKey)) {
      return Promise.resolve(componentsByKey.get(resolverKey));
    }
    const descriptor = descriptors.get(resolverKey);
    if (!descriptor) {
      return Promise.resolve(componentsByKey.get(DEFAULT_PROVIDER_KEY));
    }
    if (failedLoads.has(resolverKey)) {
      return Promise.resolve(componentsByKey.get(DEFAULT_PROVIDER_KEY));
    }
    if (!loadingPromises.has(resolverKey)) {
      var _options$translations;
      const i18nLoader = options === null || options === void 0 ? void 0 : options.i18nLoader;
      const timeoutMs = (_options$translations = options === null || options === void 0 ? void 0 : options.translationsTimeoutMs) !== null && _options$translations !== void 0 ? _options$translations : 30000;
      let timeoutHandle;
      const translationsPromise = i18nLoader && descriptor.lazyLoadedTranslations ? Promise.race([Promise.resolve().then(() => i18nLoader.register(descriptor.lazyLoadedTranslations)).then(result => {
        clearTimeout(timeoutHandle);
        return result;
      }, translationError => {
        clearTimeout(timeoutHandle);
        console.warn(`[unified-auth] Failed to load translations for "${resolverKey}":`, translationError);
      }), new Promise(resolve => {
        timeoutHandle = setTimeout(() => {
          console.warn(`[unified-auth] Translation loading timed out for "${resolverKey}", rendering without translations`);
          resolve();
        }, timeoutMs);
      })]) : Promise.resolve();
      const promise = Promise.all([descriptor.load(), translationsPromise]).then(([components]) => {
        componentsByKey.set(resolverKey, components);
        for (const descriptorAppId of descriptor.appIds) {
          providerKeyByAppId.set(Number(descriptorAppId), resolverKey);
        }
        loadingPromises.delete(resolverKey);
        return components;
      }).catch(error => {
        clearTimeout(timeoutHandle);
        loadingPromises.delete(resolverKey);
        failedLoads.add(resolverKey);
        console.error(`[unified-auth] Failed to load provider "${resolverKey}":`, error);
        return componentsByKey.get(DEFAULT_PROVIDER_KEY);
      });
      loadingPromises.set(resolverKey, promise);
    }
    return loadingPromises.get(resolverKey);
  }
  function clearRegistry() {
    var _options$onClear;
    componentsByKey.clear();
    providerKeyByAppId.clear();
    descriptors.clear();
    descriptorKeyByAppId.clear();
    loadingPromises.clear();
    failedLoads.clear();
    initialized = !(options !== null && options !== void 0 && options.initializer);
    options === null || options === void 0 || (_options$onClear = options.onClear) === null || _options$onClear === void 0 || _options$onClear.call(options);
  }
  registry = {
    registerProvider,
    unregisterProvider,
    getProviderType,
    getComponentsSync,
    resolveComponents,
    clearRegistry
  };
  return registry;
}